import * as React from 'react';
import { useMemo } from 'react';
import { differenceInDays, isBefore, isSameDay, parse, parseISO, subDays, toDate } from 'date-fns';
import { createDatesArray, isDateInRange } from '../utils';
import Day from './Day/Day';
import Slider from '../../Slider/components/Slider';
import { Currency } from '../../enums';
import { ThemeProvider, useTheme } from '../../theme';
import { getDateWithoutTimezone } from '../../Engine/components/FastSearch/utils';
import { useConfig } from '../../context';
import { API_DATE_FORMAT } from '../../utils';
import { useRenderers } from '../../renderProps';
const WeekCalendar = ({ prices, centralDate, onRangeChange, onSelect, currentMinPriceSelectedDate, isMilesSearch, allowSelectWithoutPrice, avaliableDateRange, hidePrice, minDate: minDateProp, loading }) => {
    var _a, _b;
    const { SliderStyles: sliderStyles } = useTheme('WeekCalendar');
    const calendarDaysCount = (_a = useConfig()) === null || _a === void 0 ? void 0 : _a.Engine.calendarDaysCount;
    const OverrideDay = (_b = useRenderers().CalendarDay) !== null && _b !== void 0 ? _b : Day;
    const pricesData = useMemo(() => {
        var _a, _b, _c, _d, _e, _f;
        const pricesMap = new Map();
        let minDate = null;
        let maxDate = null;
        let minPrice = {
            amount: (_c = (_b = (_a = prices[0]) === null || _a === void 0 ? void 0 : _a.price) === null || _b === void 0 ? void 0 : _b.amount) !== null && _c !== void 0 ? _c : 0,
            currency: (_f = (_e = (_d = prices[0]) === null || _d === void 0 ? void 0 : _d.price) === null || _e === void 0 ? void 0 : _e.currency) !== null && _f !== void 0 ? _f : Currency.USD
        };
        prices.forEach(price => {
            const dateTimestamp = new Date(price.date).getTime();
            if (minDate === null || dateTimestamp < minDate) {
                minDate = dateTimestamp;
            }
            if (maxDate === null || dateTimestamp > maxDate) {
                maxDate = dateTimestamp;
            }
            if (price.price && price.price.amount < minPrice.amount) {
                minPrice = Object.assign({}, price.price);
            }
            pricesMap.set(dateTimestamp, {
                date: price.date instanceof Date ? toDate(price.date) : parseISO(price.date),
                price: price.price
            });
        });
        return { pricesMap, minDate, maxDate, minPrice };
    }, [prices]);
    const { pricesMap, minDate, maxDate, minPrice } = pricesData;
    const dates = useMemo(() => {
        if (minDate === null || maxDate === null) {
            return [];
        }
        return createDatesArray(toDate(minDate), toDate(maxDate));
    }, [minDate, maxDate]);
    const parsedPricesForArrows = useMemo(() => {
        return prices.filter(item => item === null || item === void 0 ? void 0 : item.date).map(item => parse(item.date, API_DATE_FORMAT, new Date()));
    }, [prices]);
    const { today, yesterday } = useMemo(() => {
        const todayValue = new Date();
        return {
            today: todayValue,
            yesterday: subDays(todayValue, 1)
        };
    }, []);
    const hideDisabledLeftArrow = useMemo(() => {
        const minAvailableDate = avaliableDateRange === null || avaliableDateRange === void 0 ? void 0 : avaliableDateRange[0];
        return parsedPricesForArrows.some(date => {
            if (isBefore(date, today) && !isSameDay(date, today)) {
                return true;
            }
            return minAvailableDate && (isBefore(date, minAvailableDate) || isSameDay(date, minAvailableDate));
        });
    }, [today, parsedPricesForArrows, avaliableDateRange]);
    const hideDisabledRightArrow = useMemo(() => {
        const maxAvailableDate = avaliableDateRange === null || avaliableDateRange === void 0 ? void 0 : avaliableDateRange[1];
        if (!maxAvailableDate) {
            return false;
        }
        return parsedPricesForArrows.some(date => isBefore(maxAvailableDate, date) || isSameDay(date, maxAvailableDate));
    }, [parsedPricesForArrows, avaliableDateRange]);
    const samePriceAllWeek = useMemo(() => {
        const uniquePrices = new Set([...pricesMap.values()]
            .filter(dateWithPrice => { var _a; return (_a = dateWithPrice === null || dateWithPrice === void 0 ? void 0 : dateWithPrice.price) === null || _a === void 0 ? void 0 : _a.amount; })
            .map(dateWithPrice => dateWithPrice.price.amount));
        return uniquePrices.size === 1;
    }, [pricesMap]);
    const selectedDateData = useMemo(() => {
        var _a;
        const centralDateTimestamp = centralDate.getTime();
        const selectedPrice = pricesMap.get(centralDateTimestamp);
        const selectedDatePriceIsMin = ((_a = selectedPrice === null || selectedPrice === void 0 ? void 0 : selectedPrice.price) === null || _a === void 0 ? void 0 : _a.amount) === minPrice.amount;
        return { centralDateTimestamp, selectedDatePriceIsMin };
    }, [centralDate, pricesMap, minPrice.amount]);
    const onRangeChangeHandler = (direction, startIndex, itemsCount) => {
        onRangeChange(direction === 'prev' ? 'decrease' : 'increase', dates[startIndex], itemsCount);
    };
    const compareSelectedDates = (date) => {
        const normalizedDate = getDateWithoutTimezone(date);
        return (centralDate &&
            centralDate.getDate() === normalizedDate.getDate() &&
            centralDate.getMonth() === normalizedDate.getMonth());
    };
    const calculateDayProps = (date) => {
        var _a;
        const timestamp = date.getTime();
        const price = (_a = pricesMap.get(timestamp)) !== null && _a !== void 0 ? _a : null;
        const isSelected = compareSelectedDates(date);
        const dateWithoutTimezone = getDateWithoutTimezone(date);
        /**
         * WEBSKY-7248
         * Подменяем цену для выбранной даты в календаре
         * на минимальную цену с выдачи результатов поиска
         */
        const realPrice = isSelected && currentMinPriceSelectedDate ? currentMinPriceSelectedDate : price === null || price === void 0 ? void 0 : price.price;
        const isBestPrice = selectedDateData.selectedDatePriceIsMin
            ? isSelected
            : !samePriceAllWeek && (realPrice === null || realPrice === void 0 ? void 0 : realPrice.amount) === minPrice.amount;
        const isBeforeMinDate = minDateProp && isBefore(date, minDateProp);
        const canSelectWithoutPrice = (!isBeforeMinDate && isMilesSearch) || allowSelectWithoutPrice;
        const isAvailable = canSelectWithoutPrice ? !isBefore(date, yesterday) : !!realPrice;
        const isDisabled = !isAvailable || !isDateInRange(date, avaliableDateRange);
        const needHidePrice = hidePrice || isMilesSearch;
        return { dateWithoutTimezone, realPrice, isBestPrice, isSelected, isDisabled, needHidePrice };
    };
    if (!prices.length) {
        return null;
    }
    return (React.createElement(ThemeProvider, { value: { Slider: { SliderTheme: Object.assign({}, sliderStyles) } } },
        React.createElement(Slider, { maxItemsCount: calendarDaysCount, minItemWidth: 100, centeredItem: centralDate && minDate ? differenceInDays(centralDate, minDate) + 1 : 0, onRangeChange: onRangeChange && onRangeChangeHandler, disabledLeftArrow: hideDisabledLeftArrow || loading, disabledRightArrow: hideDisabledRightArrow || loading }, dates.map((date, index) => {
            const { dateWithoutTimezone, realPrice, isBestPrice, isSelected, isDisabled, needHidePrice } = calculateDayProps(date);
            return (React.createElement(OverrideDay, { key: index, date: dateWithoutTimezone, price: realPrice, isBestPrice: isBestPrice, hidePrice: needHidePrice, disabled: isDisabled, selected: isSelected, loading: loading, onSelect: onSelect }));
        }))));
};
export default WeekCalendar;
